import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { useState } from "react";

import Avatar from "../Avatar";
import { Icon } from "../Icon";
import { IconsType } from "../Icon/utils";
import Input from "../Input";
import SandboxBadge from "../SandboxBadge";

type Option = {
  avatar?: { name: string; url: string };
  icon?: IconsType;
  id: string;
  isSandbox?: boolean;
  label: string;
  value: string;
};

export default function SearchableDropdown({
  options,
  onSelect,
  buttonComponent,
  placeholder = "Search...",
  panelClassName,
}: {
  buttonComponent: React.ReactNode;
  onSelect: (value: Option) => void;
  options: Option[];
  panelClassName?: string;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <Popover as="div">
      <PopoverButton className="outline-none focus:outline-none">
        {buttonComponent}
      </PopoverButton>
      {options.length > 0 && (
        <PopoverPanel
          as="section"
          className={clsx(
            "w-[250px] z-10 left-0 lg:left-auto top-[40px] lg:top-auto  transition ease-in absolute p-2 rounded-md bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none lg:text-sm",
            panelClassName,
          )}
        >
          {options.length > 1 && (
            <Input
              type="text"
              placeholder={placeholder}
              value={query}
              onChange={(value) => setQuery(value)}
              className="mb-2 sticky"
            />
          )}
          <div className="flex flex-col overflow-auto max-h-48 md:max-h-52 lg:max-h-56 ">
            {filteredOptions.map((option) => (
              <span
                key={option.id}
                onClick={() => onSelect(option)}
                className="relative cursor-pointer text-gray-700 text-xs lg:text-sm font-normal select-none p-2 hover:bg-gray-100 rounded-md transition ease-in flex gap-2 items-center"
              >
                {option.icon && <Icon name={option.icon} className="mr-2" />}
                {option.avatar && (
                  <div className="relative shrink-0">
                    <Avatar
                      name={option.avatar.name}
                      image={option.avatar.url}
                      size={8}
                    />
                    {option.isSandbox && (
                      <SandboxBadge className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5" />
                    )}
                  </div>
                )}
                <span>{option.label}</span>
              </span>
            ))}
          </div>
        </PopoverPanel>
      )}
    </Popover>
  );
}
